import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import {
  TransferRead,
  TransferCorrect,
  TransferService,
} from "@/domains/transfer";
// components
import { Button, Form, Input, Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";

interface CorrectModalProps {
  onCheck: () => void;
}

export interface CorrectModalRef {
  open: (transfer: TransferRead) => void;
}

export const CorrectModal = forwardRef(
  ({ onCheck }: CorrectModalProps, ref: Ref<CorrectModalRef>) => {
    const [showing, setShowing] = useState(false);

    const [transfer, setTransfer] = useState<TransferRead>();

    const [form] = Form.useForm<TransferCorrect>();

    const handleConfirm = async () => {
      if (!transfer) return;

      const formValue = await form.validateFields();

      Modal.confirm({
        okType: "danger",
        title: "補單確認",
        icon: <ExclamationCircleFilled />,
        content: (
          <div>
            警告！設定實收
            <span className="mx:4 f:18 f:bold fg:error">
              {formValue.result}
            </span>
            顆，送出後無法復原
          </div>
        ),
        async onOk() {
          await TransferService.renew(transfer.id, {
            ...formValue,
            // 替代 textarea 的換行符號
            transaction: formValue.transaction
              ? formValue.transaction.replaceAll("\n", ",")
              : undefined,
          }).run();
          setShowing(false);
          onCheck();
        },
      });
    };

    const handleCancel = () => {
      setShowing(false);
    };

    useImperativeHandle(
      ref,
      () => {
        const open = (_transfer: TransferRead) => {
          form.setFieldsValue({
            result: _transfer.result,
            note: _transfer.note,
            // 替代 textarea 的換行符號
            transaction: _transfer.transaction
              ? _transfer.transaction.replaceAll(",", "\n")
              : "",
          });

          setTransfer(_transfer);

          setShowing(true);
        };

        return { open };
      },
      [form]
    );

    return (
      <Modal
        title="會員代收補單"
        open={showing}
        footer={[
          <div className="flex jc:end gap:8" key="footer">
            <Button key="back" onClick={handleCancel}>
              取消
            </Button>
            <Button key="submit" type="primary" onClick={handleConfirm}>
              送出
            </Button>
          </div>,
        ]}
        onCancel={handleCancel}
      >
        <Form form={form} labelCol={{ span: 4 }}>
          <Form.Item label="單號">
            <Input readOnly value={transfer?.id} />
          </Form.Item>

          <Form.Item label="平台單號">
            <Input readOnly value={transfer?.order?.id} />
          </Form.Item>

          <Form.Item label="會員代碼">
            <Input readOnly value={transfer?.member?.id} />
          </Form.Item>

          <Form.Item label="會員帳號">
            <Input readOnly value={transfer?.member?.username} />
          </Form.Item>

          <Form.Item label="會員名稱">
            <Input readOnly value={transfer?.member?.name} />
          </Form.Item>

          <Form.Item label="存單顆數">
            <Input readOnly type="number" value={transfer?.amount} />
          </Form.Item>

          <Form.Item
            label="實收顆數"
            name="result"
            rules={[{ required: true, message: "實收顆數不可空白" }]}
          >
            <Input type="number" placeholder="請輸入實收顆數" />
          </Form.Item>

          <Form.Item label="存單備註" name="note">
            <Input placeholder="請輸入存單備註" />
          </Form.Item>

          <Form.Item
            label="交易哈希"
            name="transaction"
            extra="若存在有多筆交易，請每筆一行輸入"
          >
            <Input.TextArea placeholder="請輸入交易哈希" rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
);
